/**
 * bpa-brief.ts — Editorial copy layer for the Best Practice Analyzer page
 * standfirst. Mirrors `posture-brief.ts` exactly in shape, seeding, and voice
 * rules. The BPA pillar tells a slightly different story: the subject is
 * "Best Practice", the named risk is the weakest starred framework or the
 * top SHALL violation, and the verdict bands key off the BPA composite
 * (`bpaScore`) rather than the Guardian composite.
 *
 * Each editorial slot has a small set of variants. A variant is picked
 * deterministically from a `${tenantId}:${localDay}:${slot}` seed so the
 * same tenant sees a stable brief through the day, and different days vary.
 *
 * The templates intentionally stay conservative — no Mad Libs, no free-form
 * generation. Word counts on the verdict line stay within a six-word budget
 * so the ScoreBox quote always fits.
 *
 * Risk-slot precedence (single source of truth — see `buildDeck` for detail):
 *   1. topAction      — when the priority-actions ranker has a #1 SHALL.
 *   2. Weakest-frame  — when one starred framework is < 70 and stands out.
 *   3. Compound       — fallback when posture is mid-band but no one item.
 *   4. Hygiene        — fallback when the score is high (>= 85).
 *
 * Loading-state handling: when `input.bpaScore` is null/undefined we return
 * a neutral "Reading practice…" brief rather than synthesizing a worst-band
 * verdict. Without this guard the composer would coerce `null → 0`, which
 * lands the loading state in the lowest editorial band and shows the user
 * an authoritative-but-wrong verdict for a fraction of a second before the
 * real data arrives. Note: `bpaScore === 0` is a legitimate real value
 * (e.g. a brand-new tenant with no starred frameworks resolved) and still
 * routes through the normal band logic — only `null`/`undefined` triggers
 * the loading brief.
 */

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface BpaBrief {
  /** Pill above the headline: "Today's Brief · 29 April" */
  kicker: string
  /** Roman portion of the headline, e.g. "Best Practice is" */
  headlineSubject: string
  /** Italic emphasis word, e.g. "strong" — gets `<em>` treatment */
  headlineModifier: string
  /** Second sentence on the headline line. Capitalized, period-terminated. */
  weakLink: string
  /** Single-paragraph deck under the headline. Uses `**bold**` markers. */
  deck: string
  /** Six-words-or-fewer verdict in the score box, italic, in quotes */
  verdict: string
  /** Byline footer: "Brief by **Guardian Continuous Assessment** · For **{user}**" */
  byline: string
  /** Score delta vs N days ago, drives the green pill */
  delta: { value: number; days: number }
}

export interface BpaFrameworkRow {
  /** Framework code, e.g. "SCuBA", "CIS", "EIDSCA", "MCSB". */
  code: string
  /** 0-100 framework score (weighted pass-rate). */
  score: number
  /** True when `includeInGuardianScore` is set (the ★ in the score box). */
  starred: boolean
  /** True for MCSB — the always-included Microsoft Cloud Security Benchmark. */
  isAlwaysIncluded: boolean
}

export interface BpaTopAction {
  /** Imperative-friendly title, e.g. "Enforce conditional access for admin roles". */
  title: string
  /** Framework citation code, e.g. "SCuBA AAD.4.1v1". */
  framework?: string
}

export interface BpaWeakestFramework {
  /** Framework code (used inside the six-word verdict cap). */
  code: string
  /** 0-100 framework score. */
  score: number
}

export interface BpaBriefInput {
  /** Currently signed-in user email, e.g. "adam.jones@iuvotech.com" */
  userEmail?: string
  /**
   * Active tenant ID. Combined with the local day to seed variant selection
   * so the brief is stable per tenant per day. Falls back to a constant when
   * absent (still deterministic, just shared across tenants).
   */
  tenantId?: string
  /** Composite BPA score (starred-frameworks weighted average), 0-100. */
  bpaScore?: number | null
  /** Per-framework rows for the score-box breakdown. May be empty. */
  frameworks?: BpaFrameworkRow[]
  /** Open SHALL violation count, score-driving frameworks only. */
  openShallCount?: number
  /** Top-ranked SHALL across starred frameworks (PriorityAction[0]). */
  topAction?: BpaTopAction
  /** Weakest framework — the one dragging the composite. */
  weakestFramework?: BpaWeakestFramework
  /** 30-day delta on the BPA composite. */
  delta30d?: { score?: number; days?: number } | null
  /** Override "today" — primarily for testing the kicker. */
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/** Format today as "Today's Brief · 29 April" using en-GB long-month. */
function formatKicker(now: Date): string {
  const fmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' })
  return `Today's Brief · ${fmt.format(now)}`
}

/** Local-day key (YYYY-MM-DD) — matches the kicker's local-time framing. */
function localDayKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** djb2 string hash. Stable across runs, no crypto needed for variant picking. */
function hash(s: string): number {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i)
  }
  return h >>> 0
}

/** Pick one variant deterministically from `(seed, slot)`. */
function pickVariant<T>(variants: readonly T[], seed: string, slot: string): T {
  if (variants.length === 0) throw new Error(`pickVariant: empty variants for slot "${slot}"`)
  const idx = hash(`${seed}|${slot}`) % variants.length
  return variants[idx]
}

/* -------------------------------------------------------------------------- */
/* Verdict variants (six-words-or-fewer per the ScoreBox cap)                 */
/* -------------------------------------------------------------------------- */

const VERDICT_EXCELLENT: readonly string[] = [
  'Practice is settled.',
  'Top form. Routine watch only.',
  'Strong across every framework.',
  'Excellent practice. Keep it steady.',
]

const VERDICT_STRONG_NO_LAGGARD: readonly string[] = [
  'Solid, with one soft spot.',
  'Healthy. One small thing to watch.',
  'Almost there. One item left.',
]

/** Templates take a `{framework}` token (framework code, single word). */
const VERDICT_STRONG_LAGGARD: readonly string[] = [
  'Strong overall. {framework} needs a hand.',
  'Almost there. Lift {framework} next.',
  'Healthy, but {framework} lags.',
]

/** Templates take a `{framework}` token (framework code, single word). */
const VERDICT_MIXED: readonly string[] = [
  'Mixed. {framework} first.',
  'Halfway there. {framework} first.',
  'Steady but soft. {framework} first.',
]

/** Generic mixed-band fallback when no topAction or weakest framework
 *  is available to name. Six words or fewer. */
const VERDICT_MIXED_NO_TARGET: readonly string[] = [
  'Mixed. Take the worst first.',
  'Halfway there. Triage broadly.',
  'Steady but soft. Triage broadly.',
]

/** Templates take a `{framework}` token (framework code, single word). */
const VERDICT_GAPS: readonly string[] = [
  'Soft floor. Start with {framework}.',
  'Several gaps. Begin with {framework}.',
  'Soft practice. {framework} first.',
]

/** No-laggard fallback for the gaps band — used when we don't have enough
 *  signal to honestly name a starting framework. Six words or fewer. */
const VERDICT_GAPS_NO_LAGGARD: readonly string[] = [
  'Soft floor. Triage broadly.',
  'Several gaps. Triage broadly.',
  'Soft practice. Triage broadly.',
]

function pickVerdict(
  bpaScore: number,
  weakest: BpaWeakestFramework | undefined,
  topAction: BpaTopAction | undefined,
  seed: string,
): string {
  if (bpaScore >= 90) return pickVariant(VERDICT_EXCELLENT, seed, 'verdict.excellent')

  if (bpaScore >= 75) {
    if (!weakest || weakest.score >= 70) {
      return pickVariant(VERDICT_STRONG_NO_LAGGARD, seed, 'verdict.strong')
    }
    const tpl = pickVariant(VERDICT_STRONG_LAGGARD, seed, 'verdict.laggard')
    return tpl.replace('{framework}', weakest.code)
  }

  if (bpaScore >= 60) {
    // Prefer the topAction's framework citation (named, single-word) when
    // available; fall back to the weakest framework code; fall back to a
    // generic no-target line so the verdict never carries `{framework}`.
    const named = topAction?.framework ?? weakest?.code
    if (named) {
      const tpl = pickVariant(VERDICT_MIXED, seed, 'verdict.mixed')
      return tpl.replace('{framework}', named)
    }
    return pickVariant(VERDICT_MIXED_NO_TARGET, seed, 'verdict.mixed.noTarget')
  }

  if (!weakest) {
    return pickVariant(VERDICT_GAPS_NO_LAGGARD, seed, 'verdict.gaps.noLaggard')
  }
  const tpl = pickVariant(VERDICT_GAPS, seed, 'verdict.gaps')
  return tpl.replace('{framework}', weakest.code)
}

/* -------------------------------------------------------------------------- */
/* Headline modifier                                                          */
/* -------------------------------------------------------------------------- */

function pickModifier(score: number, deltaValue: number): string {
  if (score >= 85) return deltaValue > 0 ? 'lifting' : 'holding'
  if (score >= 70) return deltaValue > 0 ? 'strong' : 'mixed'
  if (score >= 55) return 'weakening'
  return 'fragile'
}

/* -------------------------------------------------------------------------- */
/* Weak-link sentence                                                         */
/* -------------------------------------------------------------------------- */

const WEAKLINK_ONE: readonly string[] = [
  'One soft spot needs a hand.',
  'A single SHALL stands out.',
  'One item is dragging the score.',
]

/** Templates take an `{n}` token (word form: "Two" / "Three"). */
const WEAKLINK_FEW: readonly string[] = [
  '{n} soft spots stand out.',
  '{n} SHALLs to clean up.',
  '{n} items are dragging the score.',
]

/** Templates take a `{framework}` token (framework code). */
const WEAKLINK_DRIFT_FRAMEWORK: readonly string[] = [
  'Rapid drift in {framework}.',
  '{framework} is drifting fast.',
  '{framework} needs eyes on it this week.',
]

const WEAKLINK_DRIFT_BROAD: readonly string[] = [
  'Drift is showing up across the board.',
  'Drift is spreading across frameworks.',
  'Practice is moving the wrong way.',
]

/** Templates take a `{framework}` token (framework code). */
const WEAKLINK_LAGGARD: readonly string[] = [
  '{framework} is the soft spot today.',
  '{framework} is the one to chase first.',
  '{framework} is dragging the score down.',
]

const WEAKLINK_TIGHTEN: readonly string[] = [
  'Room to tighten.',
  'Routine cleanup remains.',
  'Hygiene work to round it out.',
]

function buildWeakLink(
  openShallCount: number | undefined,
  weakest: BpaWeakestFramework | undefined,
  seed: string,
): string {
  const numberWord = (n: number): string =>
    ['zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'][n] ?? String(n)

  if (typeof openShallCount === 'number' && openShallCount > 0) {
    if (openShallCount === 1) return pickVariant(WEAKLINK_ONE, seed, 'weakLink.one')
    if (openShallCount <= 3) {
      const tpl = pickVariant(WEAKLINK_FEW, seed, 'weakLink.few')
      return tpl.replace('{n}', numberWord(openShallCount))
    }
    if (weakest) {
      const tpl = pickVariant(WEAKLINK_DRIFT_FRAMEWORK, seed, 'weakLink.driftFramework')
      return tpl.replace('{framework}', weakest.code)
    }
    return pickVariant(WEAKLINK_DRIFT_BROAD, seed, 'weakLink.driftBroad')
  }

  if (weakest && weakest.score < 70) {
    const tpl = pickVariant(WEAKLINK_LAGGARD, seed, 'weakLink.laggard')
    return tpl.replace('{framework}', weakest.code)
  }

  return pickVariant(WEAKLINK_TIGHTEN, seed, 'weakLink.tighten')
}

/* -------------------------------------------------------------------------- */
/* Deck (trend + risk + action)                                               */
/* -------------------------------------------------------------------------- */

const TREND_LIFTING_FAST: readonly string[] = [
  'Framework alignment has moved noticeably in the right direction over the past 30 days. Real momentum here.',
  'Practice scores climbed steadily over the past month, lifted by gains across the starred frameworks.',
  'Best-practice posture has stepped up over the past 30 days. Things are moving the right way.',
]

const TREND_LIFTING: readonly string[] = [
  'Practice ticked up over the past 30 days, with steady progress across the starred frameworks.',
  'Small gains over the past 30 days, spread across the frameworks in score. Quiet progress.',
  'The score is up modestly over the past month.',
]

const TREND_FLAT: readonly string[] = [
  'Practice has held flat over the past 30 days. Not gaining ground, not losing it either.',
  "Your posture hasn't moved over the past 30 days. Same story as last month.",
  'Steady-state practice for the past month, with nothing meaningful shifting either way.',
]

const TREND_SLIPPING: readonly string[] = [
  'Practice has slipped slightly over the past 30 days. Worth a look when you can.',
  'The score is down a touch over the past month. Worth a look when you can.',
  'Modest decline over the past 30 days. Not yet a trend, but keep an eye on it.',
]

const TREND_FALLING: readonly string[] = [
  'Practice has fallen meaningfully over the past 30 days. Needs eyes on it this week.',
  'The score dropped sharply over the past month. Needs eyes on it this week.',
  'Significant decline across the past 30 days. This needs a real response, not next sprint.',
]

/** Templates take a `{phrase}` token (already-bolded action title). */
const RISK_TOP_ACTION: readonly string[] = [
  '{phrase} is the biggest move you can make today, and the one that lifts the score most.',
  '{phrase} is where your time pays back fastest right now.',
  '{phrase} is your biggest opportunity to reclaim score.',
]

/** Templates take a `{framework}` token (already-bolded framework code). */
const RISK_FRAMEWORK: readonly string[] = [
  '{framework} is the framework quietly dragging things, and the place where investment pays back fastest.',
  '{framework} is the lagging framework; investment there moves the score most.',
  '{framework} is dragging the score down today.',
]

const RISK_HYGIENE: readonly string[] = [
  'No single risk stands out. The remaining gaps are routine hygiene rather than headline failures.',
  'The remaining items are hygiene rather than headline risks.',
  "Nothing dominates the risk picture. It's housekeeping from here.",
]

const RISK_COMPOUND: readonly string[] = [
  'Several smaller gaps add up; closing the largest of them lifts the score fastest.',
  'A handful of smaller items stack up. The biggest of them is the place to start.',
  'Several smaller gaps combine to hold the score down. Tackling the biggest lifts it fastest.',
]

const ACTION_HOLD: readonly string[] = [
  "Keep doing what you're doing and let the routine reviews run on cadence.",
  "You're on the right track. Keep the routine reviews running.",
  'Keep cadence on the existing review schedule.',
]

const ACTION_LEAD: readonly string[] = [
  'Take the lead item this week to keep momentum.',
  'Knock out the lead item this week.',
  'Close the lead item this week so the score keeps moving.',
]

const ACTION_TRIAGE: readonly string[] = [
  'Take the highest-impact items first; the rest can wait until next sprint.',
  'Focus on the highest-impact items first; the rest holds until next sprint.',
  'Take the high-impact items first and let the rest wait.',
]

const ACTION_PUSH: readonly string[] = [
  'This one needs a real week of focus, not routine work.',
  'Plan a focused remediation push this sprint.',
  'This is a remediation sprint, not business-as-usual.',
]

/**
 * Risk-slot precedence (highest to lowest):
 *   1. topAction — when the priority-actions ranker has a #1 SHALL, lead with
 *      its title.
 *   2. Weakest framework — when one starred framework is < 70 and stands out.
 *   3. Hygiene (score >= 85) or Compound (everything else).
 */
function buildDeck(
  bpaScore: number,
  deltaValue: number,
  weakest: BpaWeakestFramework | undefined,
  topAction: BpaTopAction | undefined,
  seed: string,
): string {
  const trend = (() => {
    if (deltaValue >= 3) return pickVariant(TREND_LIFTING_FAST, seed, 'deck.trend.liftingFast')
    if (deltaValue > 0) return pickVariant(TREND_LIFTING, seed, 'deck.trend.lifting')
    if (deltaValue === 0) return pickVariant(TREND_FLAT, seed, 'deck.trend.flat')
    if (deltaValue >= -3) return pickVariant(TREND_SLIPPING, seed, 'deck.trend.slipping')
    return pickVariant(TREND_FALLING, seed, 'deck.trend.falling')
  })()

  const risk = (() => {
    if (topAction?.title) {
      const phrase = `**${topAction.title.replace(/[.,;:!?]+$/, '')}**`
      const tpl = pickVariant(RISK_TOP_ACTION, seed, 'deck.risk.topAction')
      return tpl.replace('{phrase}', phrase)
    }
    if (weakest && weakest.score < 70) {
      const code = `**${weakest.code}**`
      const tpl = pickVariant(RISK_FRAMEWORK, seed, 'deck.risk.framework')
      return tpl.replace('{framework}', code)
    }
    if (bpaScore >= 85) return pickVariant(RISK_HYGIENE, seed, 'deck.risk.hygiene')
    return pickVariant(RISK_COMPOUND, seed, 'deck.risk.compound')
  })()

  const action = (() => {
    if (bpaScore >= 90) return pickVariant(ACTION_HOLD, seed, 'deck.action.hold')
    if (bpaScore >= 75) return pickVariant(ACTION_LEAD, seed, 'deck.action.lead')
    if (bpaScore >= 60) return pickVariant(ACTION_TRIAGE, seed, 'deck.action.triage')
    return pickVariant(ACTION_PUSH, seed, 'deck.action.push')
  })()

  return `${trend} ${risk} ${action}`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a BpaBrief from the active BPA pillar state. All inputs are
 * optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.bpaScore` is null/undefined we return a neutral "Reading
 * practice…" brief; see the file-header docblock for rationale.
 */
export function composeBpaBrief(input: BpaBriefInput = {}): BpaBrief {
  const userEmail = input.userEmail ?? 'you'
  const now = input.asOfDate ?? new Date()
  const deltaValue = typeof input.delta30d?.score === 'number' ? input.delta30d.score : 0
  const deltaDays = typeof input.delta30d?.days === 'number' ? input.delta30d.days : 30

  const byline =
    userEmail === 'you'
      ? 'Brief by **Guardian Continuous Assessment**'
      : `Brief by **Guardian Continuous Assessment** · For **${userEmail}**`

  if (input.bpaScore == null) {
    return {
      kicker: formatKicker(now),
      headlineSubject: 'Best Practice is',
      headlineModifier: 'loading',
      weakLink: 'Reading the latest signal.',
      deck: 'Pulling the latest Microsoft 365 framework data. The brief updates once the scan resolves.',
      verdict: 'Reading practice…',
      byline,
      delta: { value: deltaValue, days: deltaDays },
    }
  }

  const bpaScore = input.bpaScore
  const seed = `${input.tenantId ?? 'guardian'}:${localDayKey(now)}`

  const headlineModifier = pickModifier(bpaScore, deltaValue)
  const weakLink = buildWeakLink(input.openShallCount, input.weakestFramework, seed)
  const verdict = pickVerdict(bpaScore, input.weakestFramework, input.topAction, seed)
  const deck = buildDeck(bpaScore, deltaValue, input.weakestFramework, input.topAction, seed)

  return {
    kicker: formatKicker(now),
    headlineSubject: 'Best Practice is',
    headlineModifier,
    weakLink,
    deck,
    verdict,
    byline,
    delta: { value: deltaValue, days: deltaDays },
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Variant tables exposed for shape-assertions in tests. Not part of the
 *  public runtime API; do not import from app code. */
export const __TEST_ONLY__ = {
  VERDICT_EXCELLENT,
  VERDICT_STRONG_NO_LAGGARD,
  VERDICT_STRONG_LAGGARD,
  VERDICT_MIXED,
  VERDICT_MIXED_NO_TARGET,
  VERDICT_GAPS,
  VERDICT_GAPS_NO_LAGGARD,
  WEAKLINK_ONE,
  WEAKLINK_FEW,
  WEAKLINK_DRIFT_FRAMEWORK,
  WEAKLINK_DRIFT_BROAD,
  WEAKLINK_LAGGARD,
  WEAKLINK_TIGHTEN,
  RISK_TOP_ACTION,
  RISK_FRAMEWORK,
  RISK_HYGIENE,
  RISK_COMPOUND,
}
